/**
 * Active-role helpers.
 *
 * Resolution priority: URL prefix > localStorage > primaryRole.
 * Per CHAP-64 + role-pattern.md § Active-role resolution priority.
 */
import type { Role, CustomerTier } from '../../shared/types.js';

const STORAGE_KEY = 'chapterbuild_active_role';

const ROLE_ACCENTS: Record<Role, string> = {
  customer: '#4a8fb8',
  team: '#f2a0b5',
  admin: '#e8a87c',
};

const TIER_LABELS: Record<CustomerTier, string> = {
  cabinet: 'Cabinet',
  board: 'Board',
  voter: 'Voter',
  observer: 'Observer',
};

const ROLE_LABELS: Record<Role, string> = {
  customer: 'Customer',
  team: 'Team',
  admin: 'Admin',
};

export function resolveActiveRole(opts: {
  url: string;
  primaryRole: Role;
  allowedRoles: Role[];
}): Role {
  // 1. URL prefix
  if (opts.url.startsWith('/admin')) {
    if (opts.allowedRoles.includes('admin')) return 'admin';
  }
  if (opts.url.startsWith('/team')) {
    if (opts.allowedRoles.includes('team')) return 'team';
  }
  // 2. localStorage
  const stored = getStoredRole();
  if (stored && opts.allowedRoles.includes(stored)) return stored;
  // 3. primaryRole
  return opts.primaryRole;
}

export function getStoredRole(): Role | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === 'customer' || v === 'team' || v === 'admin') return v;
    return null;
  } catch {
    return null;
  }
}

export function setStoredRole(role: Role, allowedRoles?: Role[]): void {
  if (allowedRoles && !allowedRoles.includes(role)) {
    throw new Error(`Role "${role}" is not in allowedRoles [${allowedRoles.join(',')}]`);
  }
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, role);
  } catch {
    // localStorage unavailable (e.g. Safari private mode); silently ignore
  }
}

export function clearStoredRole(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function roleLabel(role: Role, tier: CustomerTier | null): string {
  if (role === 'customer' && tier) {
    return `${ROLE_LABELS.customer} · ${TIER_LABELS[tier]}`;
  }
  return ROLE_LABELS[role];
}

export function roleAccent(role: Role): string {
  return ROLE_ACCENTS[role];
}

export function tierLabel(tier: CustomerTier): string {
  return TIER_LABELS[tier];
}
